import React, { useEffect, useState, createElement } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, SaveIcon, EyeIcon, Loader2Icon, CopyIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { CodeEditor } from '../components/CodeEditor';
import { PreviewModal } from '../components/PreviewModal';
import { FormPreview } from '../components/FormPreview';
import { FormBuilder } from '../components/builder/FormBuilder';
import { GptCodeAssistant } from '../components/GptCodeAssistant';
import { FormBlock, FormTheme, FormBuilderSchema, DEFAULT_FORM_THEME } from '../components/builder/types';
import { landingPagesApi } from '../services/creatorApi';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { toast } from 'sonner';
function toSlug(str: string): string {
  return str.toLowerCase().trim().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
}
export function LandingPageEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const isEditing = !!id && id !== 'new';
  const team = currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [showPreview, setShowPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [editorMode, setEditorMode] = useState<'code' | 'builder'>('code');
  const [builderBlocks, setBuilderBlocks] = useState<FormBlock[]>([]);
  const [builderTheme, setBuilderTheme] = useState<FormTheme>(DEFAULT_FORM_THEME);
  const [pageName, setPageName] = useState('');
  const [urlSlug, setUrlSlug] = useState('');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [status, setStatus] = useState('active');
  const [pageData, setPageData] = useState({
    html: '',
    css: '',
    js: ''
  });
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [activeTab, setActiveTab] = useState<'html' | 'css' | 'js'>('html');
  // Auto-generate slug from name
  useEffect(() => {
    if (!slugManuallyEdited && !isEditing) {
      setUrlSlug(toSlug(pageName));
    }
  }, [pageName, slugManuallyEdited, isEditing]);
  // Load existing page when editing
  useEffect(() => {
    if (isEditing && id) {
      setIsLoading(true);
      landingPagesApi.get(id).then(record => {
        if (record) {
          setPageName(record.Page_Name || '');
          setUrlSlug(record.Url_Slug || '');
          setSlugManuallyEdited(true);
          setStatus(record.Status || 'active');
          setPageData({
            html: record.HTML || '',
            css: record.CSS || '',
            js: record.JavaScript1 || ''
          });
          // Restore builder state if available
          if (record.Builder_Mode === 'builder' && record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
              setEditorMode('builder');
            } catch {
              /* fallback to code mode */
            }
          } else if (record.Builder_Schema) {
            try {
              const schema: FormBuilderSchema = JSON.parse(record.Builder_Schema);
              setBuilderBlocks(schema.blocks || []);
              setBuilderTheme({
                ...DEFAULT_FORM_THEME,
                ...(schema.theme || {})
              });
            } catch {
              /* ignore */
            }
          }
        } else {
          toast.error('Landing page not found');
          navigate('/landing-pages');
        }
      }).catch(err => {
        const msg = err instanceof Error ? err.message : 'Unknown error';
        toast.error('Failed to load landing page', {
          description: msg
        });
      }).finally(() => setIsLoading(false));
    }
  }, [id, isEditing, navigate]);
  const handleSave = async () => {
    if (!pageName.trim()) {
      toast.error('Page Name is required');
      return;
    }
    if (!urlSlug.trim()) {
      toast.error('URL Slug is required');
      return;
    }
    setIsSaving(true);
    const payload: Record<string, any> = {
      Page_Name: pageName,
      Url_Slug: urlSlug,
      Status: status,
      HTML: pageData.html,
      CSS: pageData.css,
      JavaScript1: pageData.js,
      Team: team,
      Builder_Mode: editorMode
    };
    // Save builder schema
    if (builderBlocks.length > 0) {
      payload.Builder_Schema = JSON.stringify({
        blocks: builderBlocks,
        theme: builderTheme
      });
    } else if (editorMode === 'builder') {
      payload.Builder_Schema = JSON.stringify({
        blocks: [],
        theme: builderTheme
      });
    }
    try {
      if (isEditing && id) {
        await landingPagesApi.update(id, payload);
        toast.success('Landing page updated', {
          description: `"${pageName}" has been updated.`
        });
      } else {
        await landingPagesApi.create(payload);
        toast.success('Landing page created', {
          description: `"${pageName}" has been saved.`
        });
      }
      if (!isEditing) {
        navigate('/landing-pages');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to save landing page', {
        description: msg
      });
    } finally {
      setIsSaving(false);
    }
  };
  const publicUrl = urlSlug ? `${window.location.origin}/page/${urlSlug}` : '';
  const handleCopyUrl = () => {
    if (!publicUrl) return;
    navigator.clipboard.writeText(publicUrl).then(() => {
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    }).catch(() => {
      const textarea = document.createElement('textarea');
      textarea.value = publicUrl;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      textarea.remove();
      setCopiedUrl(true);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(false), 2000);
    });
  };
  if (isLoading) {
    return <div className="flex items-center justify-center py-24" data-id="element-3066">
        <Loader2Icon className="h-8 w-8 text-blue-500 animate-spin" data-id="element-3067" />
        <span className="ml-3 text-sm text-gray-500" data-id="element-3068">
          Loading landing page...
        </span>
      </div>;
  }
  return <div className="space-y-6" data-id="element-3069">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-3070">
        <div className="flex items-center gap-4" data-id="element-3071">
          <Link to="/landing-pages" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-3072">
            <ArrowLeftIcon className="h-5 w-5" data-id="element-3073" />
          </Link>
          <div data-id="element-3074">
            <h1 className="text-2xl font-semibold text-gray-900" data-id="element-3075">
              {isEditing ? 'Edit Page: ' + (pageName || '...') : 'Create Landing Page'}
            </h1>
            <p className="text-sm text-gray-500" data-id="element-3076">
              {editorMode === 'builder' ? 'Drag and drop blocks to build your page.' : 'Build your landing page with custom HTML, CSS, and JS.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3" data-id="element-3077">
          {/* Mode Toggle */}
          <div className="inline-flex rounded-md border border-gray-300 bg-white p-0.5" data-id="element-3078">
            <button onClick={() => setEditorMode('code')} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'code' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3079">
              Code
            </button>
            <button onClick={() => {
            if (editorMode === 'code' && pageData.html && builderBlocks.length === 0) {
              if (!confirm('Switching to Builder mode will start fresh. Manual HTML edits cannot be imported into the builder. Continue?')) return;
            }
            setEditorMode('builder');
          }} className={`px-3 py-1.5 text-xs font-medium rounded transition-colors ${editorMode === 'builder' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-3080">
              Builder
            </button>
          </div>
          <button onClick={() => setShowPreview(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50" data-id="element-3081">
            <EyeIcon className="h-4 w-4" data-id="element-3082" /> Preview
          </button>
          <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-3083">
            {isSaving ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-3084" /> : <SaveIcon className="h-4 w-4" data-id="element-3085" />}
            {isSaving ? 'Saving...' : isEditing ? 'Update' : 'Create'}
          </button>
        </div>
      </div>

      {editorMode === 'builder' ? <div className="space-y-6" data-id="element-3086">
          <FormBuilder initialBlocks={builderBlocks} initialTheme={builderTheme} onSchemaChange={(blocks, theme) => {
        setBuilderBlocks(blocks);
        setBuilderTheme(theme);
      }} onHtmlChange={(html, css) => {
        setPageData(prev => ({
          ...prev,
          html,
          css
        }));
      }} data-id="element-3087" />
          {/* Settings below builder */}
          <div className="grid grid-cols-3 gap-6" data-id="element-3088">
            <div className="col-span-2" data-id="element-3089" />
            <div className="space-y-6" data-id="element-3090">
              <SettingsCard pageName={pageName} setPageName={setPageName} urlSlug={urlSlug} setUrlSlug={setUrlSlug} slugManuallyEdited={slugManuallyEdited} setSlugManuallyEdited={setSlugManuallyEdited} status={status} setStatus={setStatus} team={team} isEditing={isEditing} data-id="element-3091" />
              <PublicUrlCard publicUrl={publicUrl} urlSlug={urlSlug} status={status} copiedUrl={copiedUrl} onCopy={handleCopyUrl} data-id="element-3092" />
            </div>
          </div>
        </div> : <div className="grid grid-cols-3 gap-6" data-id="element-3093">
          <div className="col-span-2 space-y-6" data-id="element-3094">
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm" data-id="element-3095">
              <div className="border-b border-gray-200 px-4 py-3 flex items-center gap-4" data-id="element-3096">
                {(['html', 'css', 'js'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={'text-sm font-medium px-3 py-1.5 rounded-md transition-colors ' + (activeTab === tab ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50')} data-id="element-3097">
                    {tab === 'js' ? 'JavaScript' : tab.toUpperCase()}
                  </button>)}
              </div>
              <div className="p-4" data-id="element-3098">
                {activeTab === 'html' && <CodeEditor label="Page HTML" language="html" placeholder="<div class='landing-page'>...</div>" value={pageData.html} onChange={e => setPageData({
              ...pageData,
              html: e.target.value
            })} data-id="element-3099" />}
                {activeTab === 'css' && <CodeEditor label="Custom CSS" language="css" placeholder=".landing-page { ... }" value={pageData.css} onChange={e => setPageData({
              ...pageData,
              css: e.target.value
            })} data-id="element-3100" />}
                {activeTab === 'js' && <CodeEditor label="Custom JavaScript" language="javascript" placeholder="// Add interactivity..." value={pageData.js} onChange={e => setPageData({
              ...pageData,
              js: e.target.value
            })} data-id="element-3101" />}
              </div>
            </div>

            {/* GPT Code Assistant */}
            <GptCodeAssistant mode="form" currentCode={pageData} onCodeGenerated={code => {
          setPageData(prev => ({
            html: code.html || prev.html,
            css: code.css !== undefined ? code.css : prev.css,
            js: code.js !== undefined ? code.js : prev.js
          }));
        }} data-id="element-3102" />
          </div>

          <div className="space-y-6" data-id="element-3103">
            <SettingsCard pageName={pageName} setPageName={setPageName} urlSlug={urlSlug} setUrlSlug={setUrlSlug} slugManuallyEdited={slugManuallyEdited} setSlugManuallyEdited={setSlugManuallyEdited} status={status} setStatus={setStatus} team={team} isEditing={isEditing} data-id="element-3104" />
            <PublicUrlCard publicUrl={publicUrl} urlSlug={urlSlug} status={status} copiedUrl={copiedUrl} onCopy={handleCopyUrl} data-id="element-3105" />

            {/* Tips Card */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-3106">
              <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-3107">
                How to use
              </h3>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 space-y-2 text-xs text-blue-700" data-id="element-3108">
                <p data-id="element-3109">
                  <strong data-id="element-3110">1.</strong> Build your landing page using the code
                  editor or builder
                </p>
                <p data-id="element-3111">
                  <strong data-id="element-3112">2.</strong> Set the status to <strong data-id="element-3113">Active</strong>{' '}
                  and save
                </p>
                <p data-id="element-3114">
                  <strong data-id="element-3115">3.</strong> Go to{' '}
                  <strong data-id="element-3116">Email Templates → Edit</strong> and paste the public
                  URL into the <strong data-id="element-3117">"Redirect After Confirmation"</strong>{' '}
                  field
                </p>
                <p data-id="element-3118">
                  <strong data-id="element-3119">4.</strong> When subscribers confirm, they'll be
                  redirected to your custom landing page
                </p>
              </div>
              <p className="text-xs text-gray-500" data-id="element-3120">
                You can also select a landing page directly from the email
                template editor's redirect dropdown.
              </p>
            </div>
          </div>
        </div>}

      <PreviewModal isOpen={showPreview} onClose={() => setShowPreview(false)} title="Landing Page Preview" data-id="element-3121">
        <FormPreview html={pageData.html} css={pageData.css} data-id="element-3122" />
      </PreviewModal>
    </div>;
}
// ── Settings Card ──
function SettingsCard({
  pageName,
  setPageName,
  urlSlug,
  setUrlSlug,
  slugManuallyEdited,
  setSlugManuallyEdited,
  status,
  setStatus,
  team,
  isEditing
}: {
  pageName: string;
  setPageName: (v: string) => void;
  urlSlug: string;
  setUrlSlug: (v: string) => void;
  slugManuallyEdited: boolean;
  setSlugManuallyEdited: (v: boolean) => void;
  status: string;
  setStatus: (v: string) => void;
  team: string;
  isEditing: boolean;
}) {
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-4" data-id="element-3123">
      <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-3124">
        Settings
      </h3>
      <div className="space-y-2" data-id="element-3125">
        <label className="block text-sm font-medium text-gray-700" data-id="element-3126">
          Page Name
        </label>
        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={pageName} onChange={e => setPageName(e.target.value)} placeholder="Thank You Page" data-id="element-3127" />
      </div>
      <div className="space-y-2" data-id="element-3128">
        <label className="block text-sm font-medium text-gray-700" data-id="element-3129">
          URL Slug
        </label>
        <div className="flex rounded-md shadow-sm" data-id="element-3130">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm" data-id="element-3131">
            /page/
          </span>
          <input type="text" className="flex-1 min-w-0 block w-full px-3 py-2 rounded-none rounded-r-md border-gray-300 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" value={urlSlug} onChange={e => {
          setUrlSlug(e.target.value);
          setSlugManuallyEdited(true);
        }} placeholder="thank-you" data-id="element-3132" />
        </div>
        {!slugManuallyEdited && !isEditing && pageName && <p className="text-xs text-gray-400" data-id="element-3133">Auto-generated from page name</p>}
      </div>
      <div className="space-y-2" data-id="element-3134">
        <label className="block text-sm font-medium text-gray-700" data-id="element-3135">
          Status
        </label>
        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={status} onChange={e => setStatus(e.target.value)} data-id="element-3136">
          <option value="active" data-id="element-3137">Active</option>
          <option value="draft" data-id="element-3138">Draft</option>
          <option value="archived" data-id="element-3139">Archived</option>
        </select>
      </div>
      <div className="space-y-2" data-id="element-3140">
        <label className="block text-sm font-medium text-gray-700" data-id="element-3141">Team</label>
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed" value={team} disabled data-id="element-3142" />
        <p className="text-xs text-gray-400" data-id="element-3143">
          Automatically set from your current workspace scope
        </p>
      </div>
    </div>;
}
// ── Public URL Card ──
function PublicUrlCard({
  publicUrl,
  urlSlug,
  status,
  copiedUrl,
  onCopy
}: {
  publicUrl: string;
  urlSlug: string;
  status: string;
  copiedUrl: boolean;
  onCopy: () => void;
}) {
  if (!urlSlug) return null;
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 space-y-3" data-id="element-3144">
      <h3 className="text-sm font-medium text-gray-900 border-b border-gray-200 pb-2" data-id="element-3145">
        Public URL
      </h3>
      <div className="flex items-center gap-2" data-id="element-3146">
        <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm font-mono text-xs text-gray-600" value={publicUrl} readOnly onClick={e => (e.target as HTMLInputElement).select()} data-id="element-3147" />
        <button onClick={onCopy} className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors flex-shrink-0" data-id="element-3148">
          {copiedUrl ? <CheckIcon className="h-3.5 w-3.5" data-id="element-3149" /> : <CopyIcon className="h-3.5 w-3.5" data-id="element-3150" />}
          {copiedUrl ? 'Copied!' : 'Copy'}
        </button>
      </div>
      {status?.toLowerCase() !== 'active' && <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-md" data-id="element-3151">
          <svg className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-3152">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-3153" />
          </svg>
          <p className="text-xs text-amber-800" data-id="element-3154">
            This page is not active. Set status to <strong data-id="element-3155">Active</strong> for
            it to be publicly accessible.
          </p>
        </div>}
      {status?.toLowerCase() === 'active' && <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-800 font-medium" data-id="element-3156">
          <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-3157" />
          Open in new tab →
        </a>}
    </div>;
}